import { DinRoundProFont } from '@/lib/utils';
import Image, { StaticImageData } from 'next/image';
import React, { forwardRef } from 'react';
import { twMerge } from 'tailwind-merge';

export type InputProps = JSX.IntrinsicElements['input'] & {
  id?: string;
  wrapClassName?: string;
  inputWrapClassName?: string;
  labelClassName?: string;
  prefixClassName?: string;
  suffixClassName?: string;
  className?: string;
  errorClassName?: string;
  helpClassName?: string;
  inputPrefix?: React.ReactNode;
  inputSuffix?: React.ReactNode;
  label?: string;
  helpText?: string;
  error?: string | React.ReactNode;
  bold?: boolean;
  maxLength?: number;
  hasIcon?: boolean;
  iconName?: StaticImageData;
};

export default forwardRef<HTMLInputElement, InputProps>(function ModalInput(props, ref) {
  const [currentLength, setCurrentLength] = React.useState(0);
  const {
    id,
    wrapClassName,
    inputWrapClassName,
    labelClassName,
    prefixClassName,
    suffixClassName,
    className,
    errorClassName,
    helpClassName,
    inputPrefix,
    inputSuffix,
    label,
    helpText,
    error,
    bold,
    maxLength,
    defaultValue,
    onChange,
    hasIcon,
    iconName,
    ...rest
  } = props;

  return (
    <div className={wrapClassName}>
      <div
        className={twMerge(
          'relative flex w-full h-full justify-between items-center px-2 sm:px-2 lg:px-6 py-5 z-[2] max-h-[60px] bg-white rounded-[14px]',
          inputWrapClassName,
        )}
      >
        {inputPrefix && (
          <div
            className={twMerge(
              'flex justify-center items-center min-w-5 text-sm mr-1 sm:mr-2 z-[2]',
              prefixClassName,
            )}
          >
            {inputPrefix}
          </div>
        )}
        <div className="flex flex-col w-full min-w-0 relative z-[2]">
          {label && (
            <label
              className={twMerge(
                'text-[#7BFAFC] xl:text-[12px] text-[10px] ',
                labelClassName,
                DinRoundProFont.className,
              )}
              htmlFor={id}
            >
              {label}
            </label>
          )}
          <input
            {...rest}
            ref={ref}
            id={id}
            maxLength={maxLength}
            onChange={(e) => {
              if (onChange) {
                onChange(e);
              }
              setCurrentLength(e.target.value.length);
            }}
            autoComplete="off"
            className={twMerge(
              'w-full bg-transparent text-one-line focus:outline-none text-sm lg:text-[16px] font-semibold placeholder:text-[#CECED0] text-[#303242]',
              className,
              hasIcon ? 'pl-[50px]' : '',
            )}
          />
        </div>
        {maxLength && (
          <>
            <div className="w-[40px]" />
            <div
              className={twMerge(
                'text-#303242 flex items-center absolute right-6 top-1/2 -translate-y-1/2 z-20',
              )}
            >
              <p className="text-base font-semibold">
                {currentLength || defaultValue?.toString()?.length}/{maxLength}
              </p>
            </div>
          </>
        )}
        {inputSuffix && (
          <div
            className={twMerge(
              'min-w-5 flex justify-center items-center text-sm z-[2]',
              suffixClassName,
            )}
          >
            {inputSuffix}
          </div>
        )}
        <div className="absolute top-[3px] left-[5px] pointer-events-none w-[56px] h-[56px]">
          {hasIcon && (
            <div className="w-[56px] h-[56px]">
              <Image src={iconName} alt="Icon" className="w-full" />
            </div>
          )}
        </div>
      </div>
      {error && (
        <p
          className={twMerge(
            'text-orange-500 text-sm mt-2 text-left error-message',
            errorClassName,
          )}
        >
          {error}
        </p>
      )}
      {helpText && !error && (
        <p className={twMerge('text-white opacity-[0.5] text-sm mt-2 px-8', helpClassName)}>
          {helpText}
        </p>
      )}
    </div>
  );
});
